import { DatePicker, Form, Input, Modal, Radio, Select } from "antd";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { DepartmentCode } from "../../enum/roles";
import { ITask } from "../../models/Timesheet";
import { useGetUserByDepartmentCode } from "../../query/DepartmentQuery";
import {
  useCreateTimesheet,
  useGetExternalEmployee,
} from "../../query/TimesheetQuery";
import { notify } from "../../utils/toast";

interface ModalCreateTimesheetProps {
  open: boolean;
  onCancel: () => void;
  onFinish: () => void;
  selectedDate?: string;
}

const ModalCreateTimesheet = ({
  open,
  onCancel,
  onFinish,
  selectedDate,
}: ModalCreateTimesheetProps) => {
  const [form] = Form.useForm();
  const [userType, setUserType] = useState<"INTERNAL" | "EXTERNAL">("INTERNAL");
  const { data: users } = useGetUserByDepartmentCode(DepartmentCode.PD);
  const { data: employees, isLoading } = useGetExternalEmployee();
  const createTimesheet = useCreateTimesheet();

  useEffect(() => {
    if (open) {
      const date = selectedDate ? dayjs(selectedDate) : undefined;
      form.setFieldsValue({
        dateRange: date ? [date, date] : undefined,
        userType: "INTERNAL",
      });
      setUserType("INTERNAL");
    }
  }, [open, selectedDate, form]);

  const handleModalOk = () => {
    form.submit();
  };

  const handleFinish = async (values: any) => {
    const {
      dateRange,
      userId,
      externalResourceId,
      userType: formUserType,
      ...rest
    } = values;
    const [startDate, endDate] = dateRange;
    const startDateStr = startDate.format("YYYY-MM-DD");
    const endDateStr = endDate.format("YYYY-MM-DD");

    const payload: ITask = {
      ...rest,
      startDate: new Date(`${startDateStr}T00:00:00Z`),
      endDate: new Date(`${endDateStr}T00:00:00Z`),
      userId: formUserType === "INTERNAL" ? userId : undefined,
      externalResourceId:
        formUserType === "EXTERNAL" ? externalResourceId : undefined,
      type: formUserType === "INTERNAL" ? "INTERNAL" : "EXTERNAL",
    };

    try {
      await createTimesheet.mutateAsync(payload);
      notify.success("Tạo công việc thành công");
      onFinish();
      form.resetFields();
      setUserType("INTERNAL");
    } catch (error: any) {
      notify.error(error?.response?.data?.message || error?.message);
    }
  };

  const handleCancel = () => {
    onCancel();
    form.resetFields();
    setUserType("INTERNAL");
  };

  return (
    <Modal
      title="Tạo công việc mới"
      open={open}
      onCancel={handleCancel}
      onOk={handleModalOk}
      confirmLoading={createTimesheet.isPending}
      okText="Tạo"
      cancelText="Hủy"
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleFinish}
        className="mt-4"
      >
        <Form.Item
          label="Tên công việc"
          name="taskName"
          rules={[{ required: true, message: "Vui lòng nhập tên công việc" }]}
        >
          <Input placeholder="Nhập tên công việc" />
        </Form.Item>

        <Form.Item
          label="Mô tả"
          name="description"
          rules={[{ required: true, message: "Vui lòng nhập mô tả" }]}
        >
          <Input.TextArea placeholder="Nhập mô tả công việc" rows={4} />
        </Form.Item>
        <Form.Item label="Ghi chú" name="note">
          <Input.TextArea placeholder="Nhập ghi chú" rows={1} />
        </Form.Item>

        <Form.Item
          label="Khoảng thời gian"
          name="dateRange"
          rules={[
            { required: true, message: "Vui lòng chọn khoảng thời gian" },
          ]}
        >
          <DatePicker.RangePicker
            className="w-full"
            format="YYYY-MM-DD"
            placeholder={["Ngày bắt đầu", "Ngày kết thúc"]}
          />
        </Form.Item>

        <Form.Item label="Người duyệt" name="userType" initialValue="INTERNAL">
          <Radio.Group
            onChange={(e) => {
              setUserType(e.target.value);
            }}
          >
            <Radio value="INTERNAL">Trong hệ thống</Radio>
            <Radio value="EXTERNAL">Ngoài hệ thống</Radio>
          </Radio.Group>
        </Form.Item>
        {userType === "INTERNAL" ? (
          <Form.Item
            label="Người dùng"
            name="userId"
            rules={[{ required: true, message: "Vui lòng chọn người dùng" }]}
          >
            <Select
              placeholder="Chọn người dùng"
              options={users?.map((user) => ({
                label: user.fullName,
                value: user.id,
              }))}
            />
          </Form.Item>
        ) : (
          <Form.Item
            label="Nhân viên Part Time"
            name="externalEmployeeId"
            rules={[
              { required: true, message: "Vui lòng chọn người dùng ngoài" },
            ]}
          >
            <Select
              loading={isLoading}
              placeholder="Chọn người dùng ngoài"
              options={employees?.map((employee) => ({
                label: employee.fullName,
                value: employee.id,
              }))}
            />
          </Form.Item>
        )}
      </Form>
    </Modal>
  );
};

export default ModalCreateTimesheet;
